import { useEffect, useRef, useState } from 'react';

/**
 * Holds a map camera steady between changes of subject.
 *
 * `fitBounds` derives its zoom from the viewport, so anything that resizes the
 * map recomputes the camera and the shared `Map` moves — which is what choosing a
 * creation method did, growing the map and taking the view with it (UAT 17). This
 * only adopts a new camera when `frameKey` says the thing being framed changed.
 *
 * Nothing is held until the container has been measured: an unmeasured one fits
 * to the default US view, and latching that would strand the map there.
 *
 * @param {T} camera - The freshly computed camera.
 * @param {string} frameKey - Identity of what is being framed.
 * @param {boolean} measured - Whether the map's container has a size yet.
 * @return {T} The camera to render with.
 */
export const useFramedCamera = <T>(
  camera: T,
  frameKey: string,
  measured: boolean
): T => {
  const [held, setHeld] = useState(camera);
  const heldKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!measured || heldKeyRef.current === frameKey) {
      return;
    }
    heldKeyRef.current = frameKey;
    setHeld(camera);
  }, [measured, frameKey, camera]);

  return held;
};
